import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem('tura_user');
      return saved ? JSON.parse(saved) : null;
    } catch (_) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('tura_token');
    if (!token) {
      setLoading(false);
      return;
    }

    authAPI
      .verify()
      .then((res) => {
        if (res.success) {
          setUser(res.user);
          localStorage.setItem('tura_user', JSON.stringify(res.user));
        }
      })
      .catch(() => {
        localStorage.removeItem('tura_token');
        localStorage.removeItem('tura_user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const clearAdminImpersonation = () => {
    localStorage.removeItem('admin_impersonation');
    localStorage.removeItem('admin_original_token');
    localStorage.removeItem('admin_original_user');
  };

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login(email, password);
    if (res.success && res.token && !res.requires2FA) {
      localStorage.setItem('tura_token', res.token);
      localStorage.setItem('tura_user', JSON.stringify(res.user));
      clearAdminImpersonation();
      setUser(res.user);
    }
    return res;
  }, []);

  const loginWithToken = useCallback((token, userData) => {
    localStorage.setItem('tura_token', token);
    localStorage.setItem('tura_user', JSON.stringify(userData));
    clearAdminImpersonation();
    setUser(userData);
  }, []);

  const register = useCallback((data) => authAPI.register(data), []);

  const logout = useCallback(() => {
    authAPI.logout();
    try {
      document.cookie = 'tura_token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    } catch (_) {}
    clearAdminImpersonation();
    setUser(null);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearAdminImpersonation();
      setUser(null);
    };
    window.addEventListener('tura-auth-expired', handleAuthExpired);
    return () => window.removeEventListener('tura-auth-expired', handleAuthExpired);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.verify();
      if (res.success) {
        setUser(res.user);
        localStorage.setItem('tura_user', JSON.stringify(res.user));
        return res.user;
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
      localStorage.removeItem('tura_token');
      localStorage.removeItem('tura_user');
      clearAdminImpersonation();
      setUser(null);
    }
    return null;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithToken,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
};
